(() => {
  const root = document.documentElement;
  const body = document.body;
  if (!body) return;

  body.classList.add('badkiti-theme');

  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };

  const initReveal = () => {
    const nodes = [...document.querySelectorAll('.bk-reveal')];
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach((node) => observer.observe(node));
  };

  const initDiscountForms = () => {
    document.querySelectorAll('[data-bk-discount-form]').forEach((form) => {
      if (form.dataset.bkBound === 'true') return;
      form.dataset.bkBound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const input = form.querySelector('[name="discount"]');
        const status = form.querySelector('[data-bk-discount-status]');
        const submit = form.querySelector('button[type="submit"]');
        const code = input?.value?.trim();
        if (!code) {
          input?.focus();
          return;
        }

        const rootPath = window.Shopify?.routes?.root || '/';
        const normalizedRoot = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
        const scope = form.dataset.bkDiscountScope || 'cart';
        const isDrawer = scope === 'drawer';
        const cartItemsSection = document.getElementById('main-cart-items')?.dataset?.id;
        const cartFooterSection = document.getElementById('main-cart-footer')?.dataset?.id;
        const sections = isDrawer
          ? ['cart-drawer', 'cart-icon-bubble']
          : [cartItemsSection, cartFooterSection, 'cart-icon-bubble'].filter(Boolean);

        submit?.setAttribute('aria-busy', 'true');
        if (submit) submit.disabled = true;
        if (status) status.textContent = '';

        try {
          const response = await fetch(`${normalizedRoot}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              discount: code,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();
          if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Discount update failed');

          if (isDrawer) {
            const drawer = document.querySelector('cart-drawer');
            if (drawer?.renderContents && data.sections?.['cart-drawer']) drawer.renderContents(data);
          } else if (data.sections) {
            const parser = new DOMParser();
            const replaceSection = (key, selector) => {
              const html = data.sections?.[key];
              const current = document.querySelector(selector);
              if (!html || !current) return;
              const incoming = parser.parseFromString(html, 'text/html').querySelector(selector);
              if (incoming) current.replaceWith(incoming);
            };
            if (cartItemsSection) replaceSection(cartItemsSection, '#main-cart-items');
            if (cartFooterSection) replaceSection(cartFooterSection, '#main-cart-footer');
            const bubbleHtml = data.sections?.['cart-icon-bubble'];
            const bubble = document.getElementById('shopify-section-cart-icon-bubble');
            if (bubbleHtml && bubble) {
              const incomingBubble = parser.parseFromString(bubbleHtml, 'text/html').querySelector('#shopify-section-cart-icon-bubble');
              if (incomingBubble) bubble.replaceWith(incomingBubble);
            }
          }

          initDiscountForms();
          initCartTerms();
          initCartPageRebind();
          const refreshedStatus = document.querySelector(`[data-bk-discount-form][data-bk-discount-scope="${scope}"] [data-bk-discount-status]`);
          if (refreshedStatus) {
            const isTurkish = document.documentElement.lang?.toLowerCase().startsWith('tr');
            refreshedStatus.textContent = isTurkish
              ? 'Kod sepete kaydedildi. Uygunluğu ödeme adımında doğrulanır.'
              : 'Code saved to the cart. Eligibility is confirmed at checkout.';
          }
        } catch (error) {
          console.error(error);
          if (status) {
            const isTurkish = document.documentElement.lang?.toLowerCase().startsWith('tr');
            status.textContent = isTurkish ? 'Kod uygulanamadı. Lütfen tekrar deneyin.' : 'The code could not be applied. Please try again.';
          }
        } finally {
          submit?.removeAttribute('aria-busy');
          if (submit?.isConnected) submit.disabled = false;
        }
      });
    });
  };

  const initSearchSuggestions = () => {
    document.querySelectorAll('.search-modal').forEach((modal) => {
      const input = modal.querySelector('input[type="search"]');
      const suggestions = modal.querySelector('[data-bk-search-suggestions]');
      if (!input || !suggestions || input.dataset.bkSuggestionsBound === 'true') return;
      input.dataset.bkSuggestionsBound = 'true';
      const update = () => { suggestions.hidden = input.value.trim().length > 0; };
      input.addEventListener('input', update);
      input.addEventListener('search', update);
      modal.closest('details')?.addEventListener('toggle', update);
      update();
    });
  };

  const initCartTerms = () => {
    document.querySelectorAll('[data-bk-cart-terms]').forEach((scope) => {
      if (scope.dataset.bkBound === 'true') return;
      scope.dataset.bkBound = 'true';
      const checkbox = scope.querySelector('input[type="checkbox"]');
      const checkout = scope.closest('.drawer__inner, .cart__footer, form, body')?.querySelector('[name="checkout"]');
      if (!checkbox || !checkout) return;
      const update = () => {
        checkout.disabled = !checkbox.checked;
        checkout.setAttribute('aria-disabled', String(!checkbox.checked));
      };
      checkbox.addEventListener('change', update);
      update();
    });
  };

  const initPromoStrips = () => {
    document.querySelectorAll('[data-bk-promo]').forEach((strip) => {
      if (strip.dataset.bkBound === 'true') return;
      strip.dataset.bkBound = 'true';
      const slides = [...strip.querySelectorAll('[data-bk-promo-slide]')];
      if (slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      };
      const restart = () => {
        window.clearInterval(timer);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(() => { if (!strip.isConnected) return window.clearInterval(timer); show(index + 1); }, 6500);
      };
      strip.querySelector('[data-bk-promo-prev]')?.addEventListener('click', () => { show(index - 1); restart(); });
      strip.querySelector('[data-bk-promo-next]')?.addEventListener('click', () => { show(index + 1); restart(); });
      strip.addEventListener('focusin', () => window.clearInterval(timer));
      strip.addEventListener('focusout', restart);
      strip.addEventListener('mouseenter', () => window.clearInterval(timer));
      strip.addEventListener('mouseleave', restart);
      restart();
    });
  };

  const initSocialStrips = () => {
    document.querySelectorAll('[data-bk-social-strip]').forEach((section) => {
      if (section.dataset.bkBound === 'true') return;
      section.dataset.bkBound = 'true';
      const track = section.querySelector('[data-bk-social-track]');
      if (!track) return;
      const step = () => Math.max(220, Math.round(track.clientWidth * 0.55));
      section.querySelector('[data-bk-social-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      section.querySelector('[data-bk-social-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    });
  };

  const initStickyProduct = () => {
    const bar = document.querySelector('[data-bk-product-sticky]');
    if (!bar || bar.dataset.bkBound === 'true') return;
    bar.dataset.bkBound = 'true';

    const productSection = document.querySelector('.product');
    const priceText = bar.querySelector('[data-bk-sticky-price]');
    const picker = bar.querySelector('[data-bk-sticky-picker]');
    const pickerLabel = bar.querySelector('[data-bk-sticky-picker-label]');
    const panel = bar.querySelector('[data-bk-sticky-panel]');
    const stickySubmit = bar.querySelector('[data-bk-sticky-submit]');
    const stickySubmitLabel = bar.querySelector('[data-bk-sticky-submit-label]');
    const stickyStatus = bar.querySelector('[data-bk-sticky-status]');
    const stickyOptions = [...bar.querySelectorAll('[data-bk-sticky-option]')];
    const requiredOptionCount = Number(bar.dataset.bkStickyRequiredOptions || 0);
    const chooseLabel = bar.dataset.bkStickyChooseLabel || 'BEDEN SEÇİNİZ';
    const addLabel = bar.dataset.bkStickyAddLabel || 'SEPETE EKLE';
    const waitLabel = bar.dataset.bkStickyWaitLabel || 'BEKLEYİN';
    const selectedOptions = new Map();

    const getPrimaryButton = () => productSection?.querySelector('.product-form__submit');
    const getPrimaryForm = () => getPrimaryButton()?.closest('form');
    const getVariantPicker = () => productSection?.querySelector('variant-selects');
    const getPriceSource = () => productSection?.querySelector('[id^="price-"]');
    const getMainOption = (stickyOption) => {
      const valueId = stickyOption?.dataset.optionValueId;
      const optionName = stickyOption?.dataset.optionName;
      return [...(getVariantPicker()?.querySelectorAll('[data-option-value-id]') || [])].find((candidate) => {
        return candidate.dataset.optionValueId === valueId && candidate.dataset.optionName === optionName;
      });
    };
    const getEventOption = (target) => target?.tagName === 'SELECT' ? target.selectedOptions?.[0] : target;

    const openPanel = () => {
      if (!panel || !picker) return;
      panel.hidden = false;
      picker.setAttribute('aria-expanded', 'true');
      bar.classList.add('is-picker-open');
    };
    const closePanel = (restoreFocus = false) => {
      if (!panel || !picker) return;
      panel.hidden = true;
      picker.setAttribute('aria-expanded', 'false');
      bar.classList.remove('is-picker-open');
      if (restoreFocus) picker.focus({ preventScroll: true });
    };
    const optionIsUnavailable = (stickyOption) => {
      const mainOption = getMainOption(stickyOption);
      if (!mainOption) return true;
      return mainOption.disabled || mainOption.classList.contains('disabled');
    };
    const syncAvailability = () => {
      stickyOptions.forEach((option) => {
        const unavailable = optionIsUnavailable(option);
        option.disabled = unavailable;
        option.classList.toggle('is-unavailable', unavailable);
      });
    };
    const syncSelectionUi = () => {
      stickyOptions.forEach((option) => {
        const selected = selectedOptions.get(option.dataset.optionName) === option.dataset.optionValue;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', String(selected));
      });
      if (!pickerLabel) return;
      const values = [...selectedOptions.values()];
      pickerLabel.textContent = values.length ? values.join(' / ') : chooseLabel;
      if (values.length > 0 && values.length < requiredOptionCount) {
        pickerLabel.textContent = `${values.join(' / ')} · ${chooseLabel}`;
      }
    };
    const syncPrice = () => {
      const priceSource = getPriceSource();
      if (!priceText || !priceSource) return;
      const livePrice = priceSource.querySelector('.price') || priceSource;
      const sale = livePrice.querySelector('.price-item--sale');
      const regular = livePrice.querySelector('.price-item--regular');
      const source = livePrice.classList.contains('price--on-sale') ? sale : regular;
      const text = (source?.textContent || priceSource.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) priceText.textContent = text;
    };
    const syncSubmit = () => {
      if (!stickySubmit) return;
      const primaryButton = getPrimaryButton();
      const complete = requiredOptionCount === 0 || selectedOptions.size >= requiredOptionCount;
      const primaryUnavailable = !primaryButton || primaryButton.disabled;
      stickySubmit.disabled = !complete || primaryUnavailable;
      stickySubmit.classList.toggle('is-ready', complete && !primaryUnavailable);
      const primaryText = primaryButton?.querySelector('span')?.textContent?.replace(/\s+/g, ' ').trim();
      const waiting = complete && primaryUnavailable && primaryButton?.classList.contains('loading');
      if (stickySubmitLabel) stickySubmitLabel.textContent = waiting ? waitLabel : (primaryText || addLabel);
    };
    const sync = () => {
      syncPrice();
      syncAvailability();
      syncSelectionUi();
      syncSubmit();
    };

    const applyStickyOption = (stickyOption) => {
      if (!stickyOption || stickyOption.disabled) return;
      const mainOption = getMainOption(stickyOption);
      if (!mainOption) return;
      selectedOptions.set(stickyOption.dataset.optionName, stickyOption.dataset.optionValue);
      if (mainOption.tagName === 'OPTION') {
        const select = mainOption.closest('select');
        if (select) {
          select.value = mainOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        mainOption.click();
      }
      syncSelectionUi();
      syncSubmit();
      if (selectedOptions.size >= requiredOptionCount) closePanel(true);
    };

    picker?.addEventListener('click', () => {
      if (panel?.hidden) {
        openPanel();
        const firstAvailable = stickyOptions.find((option) => !option.disabled && !option.classList.contains('is-selected'));
        window.setTimeout(() => firstAvailable?.focus({ preventScroll: true }), 0);
      } else {
        closePanel();
      }
    });
    stickyOptions.forEach((option) => option.addEventListener('click', () => applyStickyOption(option)));
    stickySubmit?.addEventListener('click', () => {
      if (selectedOptions.size < requiredOptionCount) {
        openPanel();
        stickyStatus.textContent = chooseLabel;
        return;
      }
      const primaryButton = getPrimaryButton();
      if (primaryButton && !primaryButton.disabled) primaryButton.click();
    });
    productSection?.addEventListener('change', (event) => {
      const target = getEventOption(event.target);
      const optionName = target?.dataset?.optionName;
      if (optionName && stickyOptions.some((option) => option.dataset.optionName === optionName)) {
        selectedOptions.set(optionName, target.value);
      }
      requestAnimationFrame(sync);
    });
    document.addEventListener('click', (event) => {
      if (!bar.contains(event.target)) closePanel();
    });
    bar.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel && !panel.hidden) {
        event.preventDefault();
        closePanel(true);
      }
    });

    if (productSection && 'MutationObserver' in window) {
      new MutationObserver(() => requestAnimationFrame(sync)).observe(productSection, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'checked', 'selected'],
      });
    }

    const visibilityTrigger = getPrimaryForm() || getPrimaryButton();
    if (visibilityTrigger && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', shouldShow);
        if (!shouldShow) closePanel();
      }, { threshold: 0.05 });
      observer.observe(visibilityTrigger);
    } else {
      const handleScroll = () => {
        const shouldShow = window.scrollY > 650;
        bar.classList.toggle('is-visible', shouldShow);
        if (!shouldShow) closePanel();
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
    }
    sync();
  };

  const initDrawerRebind = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !('MutationObserver' in window)) return;
    const observer = new MutationObserver(() => {
      initDiscountForms();
      initCartTerms();
    });
    observer.observe(cartDrawer, { childList: true, subtree: true });
  };

  const initCartPageRebind = () => {
    const footer = document.getElementById('main-cart-footer');
    if (!footer || footer.dataset.bkObserverBound === 'true' || !('MutationObserver' in window)) return;
    footer.dataset.bkObserverBound = 'true';
    const observer = new MutationObserver(() => {
      initDiscountForms();
      initCartTerms();
    });
    observer.observe(footer, { childList: true, subtree: true });
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-bk-bundle-variant]');
    if (!select) return;
    const price = select.closest('[data-bk-bundle-card]')?.querySelector('[data-bk-bundle-price]');
    const option = select.selectedOptions[0];
    if (price && option) price.textContent = option.dataset.price || '';
  });

  onReady(() => {
    initReveal();
    initPromoStrips();
    initSocialStrips();
    initDiscountForms();
    initSearchSuggestions();
    initCartTerms();
    initStickyProduct();
    initDrawerRebind();
    initCartPageRebind();
    root.classList.add('badkiti-ready');
  });
  document.addEventListener('shopify:section:load', () => {
    initPromoStrips();
    initSocialStrips();
    initDiscountForms();
    initSearchSuggestions();
    initCartTerms();
  });

})();

/* Badkiti product information drawers */
(() => {
  let activeDrawer = null;
  let activeTrigger = null;
  let closeTimer = null;

  const getOverlay = () => {
    let ov = document.querySelector('[data-bk-product-drawer-overlay]');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'bk-product-drawer-overlay';
      ov.setAttribute('data-bk-product-drawer-overlay', '');
      ov.setAttribute('aria-hidden', 'true');
    }
    if (ov.parentElement !== document.body) document.body.appendChild(ov);
    return ov;
  };

  // Apply critical stacking styles inline as a fallback against theme/editor CSS.
  const forceOverlay = (ov, isOpen) => {
    if (!ov) return;
    ov.style.setProperty('position', 'fixed', 'important');
    ov.style.setProperty('inset', '0', 'important');
    ov.style.setProperty('z-index', '2147483645', 'important');
    ov.style.setProperty('display', 'block', 'important');
    ov.style.setProperty('margin', '0', 'important');
    ov.style.setProperty('padding', '0', 'important');
    ov.style.setProperty('background', 'rgba(0, 0, 0, 0.52)', 'important');
    ov.style.setProperty('opacity', isOpen ? '1' : '0', 'important');
    ov.style.setProperty('visibility', isOpen ? 'visible' : 'hidden', 'important');
    ov.style.setProperty('pointer-events', isOpen ? 'auto' : 'none', 'important');
    ov.style.setProperty('transition', 'opacity .22s ease, visibility .22s ease', 'important');
    ov.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    ov.classList.toggle('is-open', isOpen);
  };

  const closeDrawer = () => {
    if (!activeDrawer) return;
    const current = activeDrawer;
    const trigger = activeTrigger;
    const ov = getOverlay();

    current.classList.remove('is-open');
    forceOverlay(ov, false);
    document.documentElement.classList.remove('bk-product-drawer-lock');
    document.body.classList.remove('bk-product-drawer-active');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (closeTimer) window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (!current.classList.contains('is-open')) current.hidden = true;
    }, 290);

    activeDrawer = null;
    activeTrigger = null;
    trigger?.focus({ preventScroll: true });
  };

  const openDrawer = (trigger) => {
    const id = trigger.getAttribute('data-bk-product-drawer-open');
    const drawer = id ? document.getElementById(id) : null;
    if (!drawer) return;

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (activeDrawer && activeDrawer !== drawer) {
      activeDrawer.classList.remove('is-open');
      activeTrigger?.setAttribute('aria-expanded', 'false');
    }

    const ov = getOverlay();
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    drawer.style.setProperty('z-index', '2147483646', 'important');
    drawer.hidden = false;
    activeDrawer = drawer;
    activeTrigger = trigger;

    document.documentElement.classList.add('bk-product-drawer-lock');
    document.body.classList.add('bk-product-drawer-active');
    trigger.setAttribute('aria-expanded', 'true');

    // Force the overlay visible immediately, then animate the drawer.
    forceOverlay(ov, true);
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer.querySelector('[data-bk-product-drawer-close]')?.focus({ preventScroll: true });
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bk-product-drawer-open]');
    if (trigger) {
      event.preventDefault();
      openDrawer(trigger);
      return;
    }

    if (event.target.closest('[data-bk-product-drawer-close]') || event.target.closest('[data-bk-product-drawer-overlay]')) {
      event.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) closeDrawer();
    if (event.key !== 'Tab' || !activeDrawer) return;

    const focusable = [...activeDrawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter((el) => !el.hidden && el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
